import { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Building2,
  Search,
  RefreshCcw,
  Printer,
  FileSpreadsheet,
  CalendarDays,
  GitBranch,
  Layers,
  ShieldCheck,
  Landmark,
  User,
  Hash,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com";

const months = [
  { label: "Select", value: "Select" },
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 },
];

const PayrollReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // meta
  const [years, setYears] = useState(["Select"]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);

  // filters
  const [year, setYear] = useState("Select");
  const [monthNo, setMonthNo] = useState("Select");
  const [branch, setBranch] = useState("Select");
  const [departmentId, setDepartmentId] = useState("Select");
  const [pfnonpf, setPfnonpf] = useState("Select");
  const [paymode, setPaymode] = useState("Select");

  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");

  // suggestions
  const [codeSug, setCodeSug] = useState([]);
  const [nameSug, setNameSug] = useState([]);
  const [showCodeSug, setShowCodeSug] = useState(false);
  const [showNameSug, setShowNameSug] = useState(false);

  // grid
  const [rows, setRows] = useState([]);
  const [visibility, setVisibility] = useState(null); // comes from API
  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-47", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const iconWrap = `w-10 h-10 rounded-2xl flex items-center justify-center shadow ${
    isDark ? "bg-white/10" : "bg-gray-100"
  }`;

  const monthName = useMemo(() => {
    const m = months.find((x) => String(x.value) === String(monthNo));
    return m?.label || "Select";
  }, [monthNo]);

  /* ================= META ================= */
  const loadMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/payroll-report/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId || 0),
          branchType,
        },
      });

      if (res?.data?.success) {
        setYears(res.data.years || ["Select"]);
        setBranches(res.data.branches || []);
        setDepartments(res.data.departments || []);
        setVisibility(res.data.salaryStructure || null);

        setMonthNo(res.data.defaultMonth || "Select");
        setYear(res.data.defaultYear || "Select");
        setBranch(String(res.data.defaultBranchId ?? "Select"));

        setDepartmentId("Select");
        setPfnonpf("Select");
        setPaymode("Select");
      } else {
        toast.error(res?.data?.message || "Failed to load meta");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ================= DATA ================= */
  const isInvalid = (v) => v === undefined || v === null || String(v).trim() === "" || v === "Select";

  const fetchData = async (p = 1) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    // ASP requires branch + month + year
    if (isInvalid(branch) || isInvalid(monthNo) || isInvalid(year)) {
      toast.warn("Select Mandatory Fields (Branch, Month, Year)");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/payroll-report/data`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId || 0),
          branchType,

          branch,
          monthName,
          monthNo: Number(monthNo),
          year,

          departmentId: departmentId === "Select" ? "" : departmentId,
          empCode: empCode.trim(),
          empName: empName.trim(),
          pfnonpf: pfnonpf === "Select" ? "" : pfnonpf,
          paymode: paymode === "Select" ? "" : paymode,

          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
        setVisibility(res.data.visibility || visibility);
      } else {
        toast.error(res?.data?.message || "Failed to load data");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Data error");
    } finally {
      setLoading(false);
    }
  };

  // initial data after meta sets defaults
  useEffect(() => {
    if (!isInvalid(year) && !isInvalid(monthNo) && !isInvalid(branch)) {
      fetchData(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, monthNo, branch]);

  useEffect(() => {
    if (page > 1) fetchData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    fetchData(1);
  };

  const handleClear = () => {
    setDepartmentId("Select");
    setPfnonpf("Select");
    setPaymode("Select");
    setEmpCode("");
    setEmpName("");
    setPage(1);
    fetchData(1);
  };

  /* ================= AUTOCOMPLETE ================= */
  useEffect(() => {
    const t = setTimeout(async () => {
      const prefix = empCode.trim();
      if (prefix.length < 1) {
        setCodeSug([]);
        return;
      }
      if (branch === "Select") return;

      try {
        const res = await axios.get(`${BASE_URL}/api/payroll-report/empcode-suggest`, {
          params: {
            finYearId: Number(finYearId),
            branch,
            branchType,
            prefix,
          },
        });
        if (res?.data?.success) {
          setCodeSug(res.data.items || []);
        }
      } catch {
        // silent
      }
    }, 250);

    return () => clearTimeout(t);
  }, [empCode, branch, finYearId, branchType]);

  useEffect(() => {
    const t = setTimeout(async () => {
      const prefix = empName.trim();
      if (prefix.length < 1) {
        setNameSug([]);
        return;
      }
      if (branch === "Select") return;

      try {
        const res = await axios.get(`${BASE_URL}/api/payroll-report/empname-suggest`, {
          params: {
            finYearId: Number(finYearId),
            branch,
            branchType,
            prefix,
          },
        });
        if (res?.data?.success) {
          setNameSug(res.data.items || []);
        }
      } catch {
        // silent
      }
    }, 250);

    return () => clearTimeout(t);
  }, [empName, branch, finYearId, branchType]);

  /* ================= PRINT / EXCEL ================= */
const handlePrint = () => {
  if (isInvalid(branch) || isInvalid(monthNo) || isInvalid(year)) {
    toast.warn("Select Mandatory Fields (Branch, Month, Year)");
    return;
  }

  const qs = new URLSearchParams({
    finYearId,
    branch,
    month: monthName || "",          // ✅ ADD THIS (print expects month)
    monthName: monthName || "",      // keep
    monthNo: String(monthNo || ""),  // keep
    year: String(year || ""),
    departmentId: departmentId === "Select" ? "" : (departmentId || ""),
    empCode: (empCode || "").trim(),
    empName: (empName || "").trim(),
    pfnonpf: pfnonpf === "Select" ? "" : (pfnonpf || ""),
    paymode: paymode === "Select" ? "" : (paymode || ""),
  });

  window.open(`/PayRoll/PayrollReport_Print?${qs.toString()}`, "_blank");
};


  const handleExcel = () => {
    if (isInvalid(branch) || isInvalid(monthNo) || isInvalid(year)) {
      toast.warn("Select Mandatory Fields (Branch, Month, Year)");
      return;
    }

    const qs = new URLSearchParams({
      finYearId,
      branch,
      monthName,
      year,
      departmentId: departmentId === "Select" ? "" : departmentId,
      empCode: empCode.trim(),
      empName: empName.trim(),
      pfnonpf: pfnonpf === "Select" ? "" : pfnonpf,
      paymode: paymode === "Select" ? "" : paymode,
    });

    window.open(`${BASE_URL}/api/payroll-report/excel?${qs.toString()}`);
  };

  /* ================= table columns visibility ================= */
  const col = {
    grade: visibility?.gradeVisible !== false,
    paidbasic: visibility?.paidbasicVisible !== false,
    conveyance: visibility?.conveyanceVisible !== false,
    educational: visibility?.educationalVisible !== false,
    medical: visibility?.medicalVisible !== false,
    hra: visibility?.hraVisible !== false,
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE PAYROLL REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={iconWrap}>
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Payroll Report Listing
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Filter / Search / Pagination / Excel / Print
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchData(page)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handleExcel} className={ghostBtn} disabled={loading}>
                <FileSpreadsheet className="w-4 h-4" />
                Excel
              </button>

              <button onClick={handlePrint} className={primaryBtn} disabled={loading}>
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>

          {/* FILTER FORM */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {/* Year */}
              <div>
                <label className={labelBase}>Year *</label>
                <div className="mt-2 flex items-center gap-2">
                  <CalendarDays className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select value={year} onChange={(e) => setYear(e.target.value)} className={inputBase}>
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Month */}
              <div>
                <label className={labelBase}>Month *</label>
                <div className="mt-2 flex items-center gap-2">
                  <CalendarDays className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select
                    value={monthNo}
                    onChange={(e) => setMonthNo(e.target.value)}
                    className={inputBase}
                  >
                    {months.map((m) => (
                      <option key={m.label} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Branch */}
              <div>
                <label className={labelBase}>Branch *</label>
                <div className="mt-2 flex items-center gap-2">
                  <GitBranch className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select value={branch} onChange={(e) => setBranch(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    {branches.map((b) => (
                      <option key={b.fld_branchid} value={b.fld_branchid}>
                        {b.fld_branchname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Department */}
              <div>
                <label className={labelBase}>Department</label>
                <div className="mt-2 flex items-center gap-2">
                  <Layers className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select
                    value={departmentId}
                    onChange={(e) => setDepartmentId(e.target.value)}
                    className={inputBase}
                  >
                    <option value="Select">Select</option>
                    {departments.map((d) => (
                      <option key={d.fld_deptid} value={d.fld_deptid}>
                        {d.fld_departmentname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* PF/NonPF */}
              <div>
                <label className={labelBase}>PF/Non PF</label>
                <div className="mt-2 flex items-center gap-2">
                  <ShieldCheck className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select value={pfnonpf} onChange={(e) => setPfnonpf(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Honorarium">Honorarium</option>
                  </select>
                </div>
              </div>

              {/* Pay mode */}
              <div>
                <label className={labelBase}>Payment Mode</label>
                <div className="mt-2 flex items-center gap-2">
                  <Landmark className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select value={paymode} onChange={(e) => setPaymode(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    <option value="Cash">Cash</option>
                    <option value="Bank">Bank</option>
                  </select>
                </div>
              </div>

              {/* Emp code (with suggest) */}
              <div className="relative">
                <label className={labelBase}>Employee Code</label>
                <div className="mt-2 flex items-center gap-2">
                  <Hash className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    value={empCode}
                    onChange={(e) => {
                      setEmpCode(e.target.value);
                      setShowCodeSug(true);
                    }}
                    onBlur={() => setTimeout(() => setShowCodeSug(false), 150)}
                    placeholder="Type employee code..."
                    className={inputBase}
                  />
                </div>

                {showCodeSug && codeSug.length > 0 && (
                  <div className={`absolute z-20 mt-2 w-full rounded-2xl border shadow-lg overflow-hidden ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                    {codeSug.slice(0, 10).map((it, idx) => (
                      <div
                        key={idx}
                        className={`px-4 py-2 cursor-pointer ${isDark ? "hover:bg-white/10" : "hover:bg-gray-50"}`}
                        onMouseDown={() => {
                          setEmpCode(it);
                          setShowCodeSug(false);
                        }}
                      >
                        {it}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Emp name (with suggest) */}
              <div className="relative">
                <label className={labelBase}>Employee Name</label>
                <div className="mt-2 flex items-center gap-2">
                  <User className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    value={empName}
                    onChange={(e) => {
                      setEmpName(e.target.value);
                      setShowNameSug(true);
                    }}
                    onBlur={() => setTimeout(() => setShowNameSug(false), 150)}
                    placeholder="Type employee name..."
                    className={inputBase}
                  />
                </div>

                {showNameSug && nameSug.length > 0 && (
                  <div className={`absolute z-20 mt-2 w-full rounded-2xl border shadow-lg overflow-hidden ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                    {nameSug.slice(0, 10).map((it, idx) => (
                      <div
                        key={idx}
                        className={`px-4 py-2 cursor-pointer ${isDark ? "hover:bg-white/10" : "hover:bg-gray-50"}`}
                        onMouseDown={() => {
                          setEmpName(it);
                          setShowNameSug(false);
                        }}
                      >
                        {it}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-3 justify-center mt-6">
              <button type="submit" className={primaryBtn} disabled={loading}>
                <Search className="w-4 h-4" />
                Search
              </button>

              <button type="button" onClick={handleClear} className={ghostBtn} disabled={loading}>
                <RefreshCcw className="w-4 h-4" />
                Clear Filters
              </button>
            </div>
          </form>

          {/* TABLE */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold">Designation</th>

                    <th className="px-4 py-4 text-center font-bold">Gross Salary</th>
                    <th className="px-4 py-4 text-center font-bold">Basic Pay</th>
                    <th className="px-4 py-4 text-center font-bold">LOP Days</th>

                    <th className="px-4 py-4 text-center font-bold">Paid Basic</th>
                    <th className="px-4 py-4 text-center font-bold">Conveyance</th>
                    <th className="px-4 py-4 text-center font-bold">Educational</th>
                    <th className="px-4 py-4 text-center font-bold">HRA</th>

                    <th className="px-4 py-4 text-center font-bold">PF Type</th>
                    <th className="px-4 py-4 text-center font-bold">PF Amount</th>
                    <th className="px-4 py-4 text-center font-bold">ESI</th>

                    <th className="px-4 py-4 text-center font-bold">TDS</th>
                    <th className="px-4 py-4 text-center font-bold">Others</th>

                    <th className="px-4 py-4 text-center font-bold">Loan</th>
                    <th className="px-4 py-4 text-center font-bold">Loan Deduction</th>
                    <th className="px-4 py-4 text-center font-bold">Loan Balance</th>

                    <th className="px-4 py-4 text-center font-bold">Net Salary</th>
                    <th className="px-4 py-4 text-center font-bold">Branch Name</th>

                    <th className="px-4 py-4 text-center font-bold">Payment Month</th>
                    <th className="px-4 py-4 text-center font-bold">Pay Mode</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        <td colSpan={23} className="px-4 py-4">
                          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                        </td>
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={23}
                        className={`px-4 py-12 text-center ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={idx}
                        className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${
                          isDark ? "bg-gray-800" : "bg-gray-50"
                        }`}
                      >
                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_employeecode}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_employee}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_designationname}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {Number(r.fld_grosssalary || 0).toFixed(2)}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {Number(r.fld_basicpay || 0).toFixed(2)}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {Number(r.fld_lopdays || 0).toFixed(2)}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {Number(r.fld_paidbasic || 0).toFixed(2)}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {Number(r.fld_conveyance || 0).toFixed(2)}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {Number(r.fld_educational || 0).toFixed(2)}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {Number(r.fld_hra || 0).toFixed(2)}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_pf}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {Number(r.pfamt || 0).toFixed(2)}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {Number(r.fld_esi || 0).toFixed(2)}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {Number(r.fld_tds || 0).toFixed(2)}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {Number(r.fld_advance || 0).toFixed(2)}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {Number(r.fld_loanamt || 0).toFixed(2)}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {Number(r.fld_loandetection || 0).toFixed(2)}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {Number(r.loandetection || 0).toFixed(2)}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {Number(r.fld_Netpay || 0).toFixed(2)}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_branchname}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.paymentmonth}
                        </td>

                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.paymode || ""}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* PAGINATION */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === i + 1
                        ? `${primaryBtnClass} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default PayrollReport;
